import { useState, useRef } from 'react'

import paymentMethodLabel from '../paymentMethodLabel'
import { genericError } from './useRecord'
import { subscriptionIntervals } from './useStripeServices'

const usePaypal = ({
  i18n, donation, record, paymentOption, paypal, finished, failed
}) => {
  const [ showSummary, setShowSummary ] = useState(false)
  const [ loading, setLoading ] = useState(false)
  const [ planId, setPlanId ] = useState()
  const [ idToken, setIdToken ] = useState()
  const cachedPlans = useRef({})
  // The draft saved on Continue. A ref, since the PayPal buttons hold on to
  // the callbacks from when they rendered.
  const draft = useRef()

  const showPlanSummary = async () => {
    const plan = JSON.stringify({
      ...subscriptionIntervals[donation.state.subscription],
      amount: donation.total,
      currency: donation.currency.iso_code,
      descriptor: "Pledge donation subscription"
    })

    const cachedPlanId = cachedPlans.current[plan]

    if (cachedPlanId) {
      setPlanId(cachedPlanId)
      setShowSummary(true)
      return
    }

    try {
      const response = await fetch(paypal.plan_path, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: plan
      })
      const json = await response.json()
      if (!response.ok) {
        console.warn(json)
        throw new Error('Validation or server error')
      }

      cachedPlans.current[plan] = json.id
      setPlanId(json.id)
      setIdToken(json.id_token)
      setShowSummary(true)
    } catch (error) {
      console.error('Error creating PayPal plan:', error)
      failed(genericError(i18n))
    }
  }

  // Saves the draft before any PayPal call, so the PayPal order is created
  // for it (Summary's createOrder sends draftToken) and can be traced back
  // to it. The donation can't change while the summary is up; dismissing
  // it and pressing Continue again saves over the same draft.
  const init = async () => {
    if (loading) return

    setLoading(true)

    try {
      const saved = await record.saveDraft({
        order: {
          payment_method_used: paymentMethodLabel(paymentOption),
          payment_source: paymentOption,
        }
      })

      if (saved.errors) return failed(saved.errors)

      draft.current = saved

      if (donation.state.subscription == 'one_time')
        setShowSummary(true)
      else
        await showPlanSummary()
    } finally {
      setLoading(false)
    }
  }

  const draftToken = () => draft.current?.token

  // Called as soon as PayPal hands back a recurring gift's approval, since
  // Summary then saves the vault token and charges the first month
  // (createOrder) before process() runs. Covering the panel from there keeps
  // the donor from starting a second attempt while the first is charging.
  const startCharge = () => record.setProcessing(true)

  const process = async ({ orderID, paymentToken, customerId }) => {
    // Mark the panel processing for the capture window (onApprove →
    // capture). This drives the "do_not_hit_back" Backdrop and the modal
    // close-lock; every exit below routes through the shared
    // finished/failed, which clear it.
    record.setProcessing(true)

    const { token, serializedDonation } = draft.current

    const params = {
      order_id: token,
      processing_id: orderID
    }

    // Add payment token if vault was used
    if (paymentToken) {
      params.subscription = {
        plan_id: planId,
        customer_id: customerId,
        payment_token: paymentToken,
      }
    }

    try {
      const response = await fetch(paypal.process_path, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(params)
      })
      if (!response.ok) {
        const json = await response.json()
        console.warn(json)
        throw new Error('Validation or server error')
      }

      finished({ token, serializedDonation })
    } catch (error) {
      console.error('Error processing PayPal payment:', error)
      failed(genericError(i18n))
    } finally {
      setLoading(false)
    }
  }

  return {
    idToken, showSummary, setShowSummary, loading, init, draftToken, startCharge, process, failed,
  }
}

export default usePaypal
